import {
  FormApproveRejectRequest,
  FormApproveRejectResponse,
  FormBatchApproveRejectRequest,
  ItReqApproveRejectRequest,
  ItReqBatchApproveRejectRequest,
} from '@Types/api/form';
import { ApprovalsListResponse } from '@Types/api/approvalsList';
import { ApiBaseName, ApiReducerPath } from '@Enum/index';
import { createApi } from '@reduxjs/toolkit/query/react';
import { SortOrder } from '@Types/common/SortOrder';

import { getFirstResultIndex } from './utils/getFirstResultIndex';
import { TABLE_PAGE_DEFAULT } from '@Features/constants';
import { OrQuery, SearchCriteria } from '@Types/api/common/filters';
import { TaskCount } from '@Types/common/TaskCount';
import { RequisitionBatchApproveRejectRequest } from '@Types/api/form/materialRequisition/Approve/RequisitionBatchApproveRequest';
import { RequisitionApproveRejectRequest } from '@Types/api/form/materialRequisition/Approve/RequisitionApproveRequest';
import { customBaseQuery } from '@Utils/api/customBaseQuery';
import {
  RequestListSearchRequest,
  RequestListSearchResponse,
} from '@Types/api/requestList';
import {
  ApprovalsTasksRequisitionResponse,
  ApprovalsTasksResponse,
} from '@Types/api/approvalsList/ApprovalsListResponse';

const prepareProcessVariables = (
  filters: SearchCriteria[] | undefined,
  requestTypeId: number | undefined
): SearchCriteria[] | undefined => {
  if (!requestTypeId) {
    return filters;
  }

  const reqTypeFilter: SearchCriteria = {
    name: 'requestTypeID',
    operator: 'eq',
    value: requestTypeId,
  };

  const output = [reqTypeFilter, ...(filters || [])];
  return output;
};

const defaultServiceFilters = (
  services: Array<'ESS' | 'IT' | 'RESIGNITION' | 'MR'>
) => ({
  name: 'service',
  value: services,
  operator: 'in',
});

export const approvalsListApi = createApi({
  reducerPath: ApiReducerPath.API_APPROVALS,
  tagTypes: ['taskList', 'widgetTaskList', 'approvalTaskList'],
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    getTasksCount: builder.mutation<TaskCount[], void>({
      query: () => ({
        url: `${ApiBaseName.ESS}/approval-tasks/count`,
        method: 'get',
      }),
    }),
    reassign: builder.mutation<
      null,
      {
        taskId: string;
        assigneeId: string;
      }
    >({
      query: ({ taskId, assigneeId }) => ({
        url: `${ApiBaseName.COMMUNDA_EXT}/camunda-extension/task/reassign`,
        method: 'post',
        body: {
          taskId,
          assigneeId,
        },
      }),
    }),
    getTaskList: builder.query<
      Array<ApprovalsListResponse>,
      {
        filters?: SearchCriteria[];
        requestTypeId?: number;
        searchValue?: string;
        assignee: string;
        pageSize: number;
        page?: number;
        sortBy?: string;
        sortOrder?: SortOrder;
        createdBefore?: string;
        createdAfter?: string;
        orQueries?: OrQuery[];
        reqService?: 'ESS' | 'REQUISITION' | 'NO-FILTER';
        processDefinitionName?: string;
        processInstanceBusinessKey?: number | string;
      }
    >({
      providesTags: ['taskList'],
      query: ({
        filters,
        requestTypeId,
        searchValue,
        assignee,
        pageSize,
        page = TABLE_PAGE_DEFAULT,
        sortBy = 'recordId',
        sortOrder = 'desc',
        createdBefore,
        createdAfter,
        orQueries,
        processDefinitionName,
        processInstanceBusinessKey,
      }) => ({
        url: `${ApiBaseName.COMMUNDA_EXT}/camunda-extension/task`,
        method: 'post',
        body: {
          processVariables: prepareProcessVariables(filters, requestTypeId),
          assignee,
          active: true,
          createdBefore,
          createdAfter,
          orQueries,
          processDefinitionName,
          processInstanceBusinessKey,
        },
        params: {
          firstResult: getFirstResultIndex(pageSize, page),
          maxResults: pageSize,
          searchValue,
          sortBy,
          sortOrder,
        },
      }),
      transformResponse(
        response: Array<ApprovalsListResponse>,
        _meta,
        { reqService = 'NO-FILTER' }
      ) {
        if (reqService !== 'NO-FILTER') {
          const filtredResponse = response.filter(
            (task) =>
              task.variables?.requestService?.value.toLocaleLowerCase() ===
              reqService.toLocaleLowerCase()
          );

          return filtredResponse;
        }
        return response;
      },
    }),

    getApprovalTaskListRequisition: builder.mutation<
      RequestListSearchResponse<ApprovalsTasksRequisitionResponse>,
      RequestListSearchRequest
    >({
      query: ({
        searchValue,
        filters = [],
        page = TABLE_PAGE_DEFAULT,
        size = 9,
        sortBy = 'reqId',
        sortOrder = 'desc',
      }) => ({
        url: `${ApiBaseName.REQUISITION}/approval-tasks/search`,
        method: 'post',
        body: {
          searchValue: searchValue,
          searchCriteriaList: [
            {
              searchCriterias: [...filters],
              dataOperator: 'and',
            },
          ],
          dataOperator: 'and',
        },
        params: { page, size, 'sort-by': sortBy, 'sort-order': sortOrder },
      }),
    }),

    getApprovalTaskList: builder.mutation<
      RequestListSearchResponse<ApprovalsTasksResponse>,
      RequestListSearchRequest & {
        services: Array<'ESS' | 'IT' | 'RESIGNITION' | 'MR'>;
      }
    >({
      query: ({
        searchValue,
        filters = [],
        page = TABLE_PAGE_DEFAULT,
        size = 9,
        sortBy = 'requestId',
        sortOrder = 'desc',
        services = ['ESS', 'IT', 'RESIGNITION'],
      }) => ({
        url: `${ApiBaseName.ESS}/approval-tasks/search`,
        method: 'post',
        body: {
          searchValue: searchValue,
          searchCriteriaList: [
            {
              searchCriterias: [defaultServiceFilters(services), ...filters],
              dataOperator: 'and',
            },
          ],
          dataOperator: 'and',
        },
        params: { page, size, 'sort-by': sortBy, 'sort-order': sortOrder },
      }),
    }),
    getTaskListByRecordId: builder.mutation<
      Array<ApprovalsListResponse>,
      {
        filters?: SearchCriteria[];
        requestTypeId?: number;
        searchValue?: string;
        assignee: string;
        pageSize: number;
        page?: number;
        sortBy?: string;
        sortOrder?: SortOrder;
        createdBefore?: string;
        createdAfter?: string;
        orQueries?: OrQuery[];
        reqService?: 'ESS' | 'REQUISITION' | 'NO-FILTER';
        processDefinitionName?: string;
        processInstanceBusinessKey?: number | string;
      }
    >({
      query: ({
        filters,
        requestTypeId,
        searchValue,
        assignee,
        pageSize,
        page = TABLE_PAGE_DEFAULT,
        sortBy = 'recordId',
        sortOrder = 'desc',
        createdBefore,
        createdAfter,
        orQueries,
        processDefinitionName,
        processInstanceBusinessKey,
      }) => ({
        url: `${ApiBaseName.COMMUNDA_EXT}/camunda-extension/task`,
        method: 'post',
        body: {
          processVariables: prepareProcessVariables(filters, requestTypeId),
          assignee,
          active: true,
          createdBefore,
          createdAfter,
          orQueries,
          processDefinitionName,
          processInstanceBusinessKey,
        },
        params: {
          firstResult: getFirstResultIndex(pageSize, page),
          maxResults: pageSize,
          searchValue,
          sortBy,
          sortOrder,
        },
      }),
      transformResponse(
        response: Array<ApprovalsListResponse>,
        _meta,
        { reqService = 'NO-FILTER' }
      ) {
        if (reqService !== 'NO-FILTER') {
          const filtredResponse = response.filter(
            (task) =>
              task.variables?.requestService?.value.toLocaleLowerCase() ===
              reqService.toLocaleLowerCase()
          );
          return filtredResponse;
        }
        return response;
      },
    }),
    getTaskListDashboard: builder.query<
      Array<ApprovalsListResponse>,
      { reqService?: 'ESS' | 'REQUISITION' | 'NO-FILTER' }
    >({
      providesTags: ['widgetTaskList'],
      query: () => ({
        url: `${ApiBaseName.COMMUNDA_EXT}/camunda-extension/dashboard-task`,
        method: 'get',
      }),
      transformResponse(
        response: Array<ApprovalsListResponse>,
        _meta,
        { reqService = 'NO-FILTER' }
      ) {
        if (reqService !== 'NO-FILTER') {
          const filtredResponse = response.filter(
            (task) =>
              task.variables?.requestService?.value.toLocaleLowerCase() ===
              reqService.toLocaleLowerCase()
          );
          return filtredResponse;
        }
        return response;
      },
    }),
    getTaskListTotal: builder.query<
      Array<ApprovalsListResponse>,
      {
        assignee: string;
        searchValue?: string;
        filters?: SearchCriteria[];
        maxResults?: number;
        requestTypeId?: number;
        createdBefore?: string;
        createdAfter?: string;
        orQueries?: OrQuery[];
        reqService?: 'ESS' | 'REQUISITION' | 'NO-FILTER';
        processDefinitionName?: string;
      }
    >({
      providesTags: ['widgetTaskList'],
      query: ({
        assignee,
        searchValue,
        filters,
        requestTypeId,
        createdBefore,
        createdAfter,
        orQueries,
        maxResults = 100,
        processDefinitionName,
      }) => ({
        url: `${ApiBaseName.COMMUNDA_EXT}/camunda-extension/task`,
        method: 'post',
        body: {
          assignee,
          processVariables: prepareProcessVariables(filters, requestTypeId),
          active: true,
          createdBefore,
          createdAfter,
          orQueries,
          maxResults,
          processDefinitionName,
        },
        params: { searchValue },
      }),
      transformResponse(
        response: Array<ApprovalsListResponse>,
        _meta,
        { reqService = 'NO-FILTER' }
      ) {
        if (reqService !== 'NO-FILTER') {
          const filtredResponse = response.filter(
            (task) =>
              task.variables?.requestService?.value.toLocaleLowerCase() ===
              reqService.toLocaleLowerCase()
          );
          return filtredResponse;
        }
        return response;
      },
    }),
    formApprove: builder.mutation<
      FormApproveRejectResponse,
      {
        body: FormApproveRejectRequest;
        servicePathName: string;
      }
    >({
      query: ({ body, servicePathName }) => ({
        url: `${servicePathName}/command`,
        method: 'post',
        body,
      }),
      invalidatesTags: ['taskList', 'widgetTaskList', 'approvalTaskList'],
    }),
    batchApprove: builder.mutation<
      FormApproveRejectResponse,
      { servicePathname: string; body: FormBatchApproveRejectRequest }
    >({
      query: ({ servicePathname, body }) => ({
        url: `${servicePathname}/commands`,
        method: 'post',
        body,
      }),
      invalidatesTags: ['taskList', 'widgetTaskList', 'approvalTaskList'],
    }),
    itRequisitionAprrove: builder.mutation<
      FormApproveRejectResponse,
      ItReqApproveRejectRequest
    >({
      query: (body) => ({
        url: `${ApiBaseName.ESS}/it-requisition/command`,
        method: 'post',
        body: body,
      }),
      invalidatesTags: ['taskList', 'widgetTaskList', 'approvalTaskList'],
    }),
    itRequisitionBatchApprove: builder.mutation<
      FormApproveRejectResponse,
      ItReqBatchApproveRejectRequest
    >({
      query: (body) => ({
        url: `${ApiBaseName.ESS}/it-requisition/commands`,
        method: 'post',
        body,
      }),
      invalidatesTags: ['taskList', 'widgetTaskList', 'approvalTaskList'],
    }),
    requisitionAprrove: builder.mutation<
      FormApproveRejectResponse,
      RequisitionApproveRejectRequest
    >({
      query: (body) => ({
        url: `${ApiBaseName.REQUISITION}/requisition/approve`,
        method: 'post',
        body: body,
      }),
      invalidatesTags: ['taskList', 'widgetTaskList', 'approvalTaskList'],
    }),
    requisitionBatchApprove: builder.mutation<
      FormApproveRejectResponse,
      RequisitionBatchApproveRejectRequest
    >({
      query: (body) => ({
        url: `${ApiBaseName.REQUISITION}/requisition/approve-batch`,
        method: 'post',
        body,
      }),
      invalidatesTags: ['taskList', 'widgetTaskList', 'approvalTaskList'],
    }),
  }),
});

export const {
  useGetApprovalTaskListRequisitionMutation, // Requisition Approval Tasks List
  useGetApprovalTaskListMutation, // ESS Approval Tasks List
  useGetTaskListDashboardQuery,
  useGetTasksCountMutation,
  useReassignMutation,
  useGetTaskListQuery,
  useGetTaskListByRecordIdMutation,
  useFormApproveMutation,
  useGetTaskListTotalQuery,
  useBatchApproveMutation,
  useItRequisitionAprroveMutation,
  useItRequisitionBatchApproveMutation,
  useRequisitionAprroveMutation,
  useRequisitionBatchApproveMutation,
  useLazyGetTaskListQuery,
} = approvalsListApi;
